/**
 * Client-side K-means clustering with K-means++ initialization and cosine distance.
 *
 * Designed for 384-dimensional embedding vectors (sentence-transformers output).
 * Runs entirely in the browser on up to 2000 vectors.
 *
 * @see FRD 869 — Agentic AI Thematic Analysis
 */

export interface KMeansConfig {
  /** Number of clusters */
  k: number;
  /** Maximum iterations (default: 100) */
  maxIterations?: number;
  /** Convergence tolerance — max centroid movement (default: 1e-4) */
  tolerance?: number;
  /** Number of times to run K-means with different centroid seeds (default: 5) */
  nInit?: number;
  /** Target dimensions for random projection (default: 32). Set to 0 to disable. */
  targetDims?: number;
}

export interface KMeansResult {
  /** k centroids, each 384 dimensions */
  centroids: Float32Array[];
  /** Cluster assignment index for each input vector */
  assignments: number[];
  /** Number of iterations until convergence */
  iterations: number;
  /** Sum of squared cosine distances to assigned centroids */
  inertia: number;
}

export interface ClusterSuggestion {
  /** Cluster index (0 to k-1) */
  id: number;
  /** 10 nearest traits to centroid */
  topTraits: {
    text: string;
    type: 'strength' | 'weakness';
    /** Cosine similarity to centroid (0-1) */
    similarity: number;
  }[];
}

/**
 * Simple seeded pseudo-random number generator (Mulberry32).
 * Used to ensure deterministic clustering results.
 */
function mulberry32(a: number) {
  return function () {
    let t = (a += 0x6d2b79f5);
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// Global seeded random function for this module
let seededRandom = mulberry32(12345);

/**
 * Reset the random seed to ensure deterministic runs.
 */
export function resetKMeansSeed(seed: number = 12345) {
  seededRandom = mulberry32(seed);
}

/**
 * Returns the suggested k range based on sample size.
 */
export function getKRange(sampleSize: number): { min: number; max: number; default: number } {
  if (sampleSize <= 50) {
    return { min: 3, max: 5, default: 4 };
  }
  if (sampleSize <= 200) {
    return { min: 5, max: 10, default: 7 };
  }
  // if (sampleSize <= 500) {
  //   return { min: 8, max: 15, default: 10 };
  // }
  // if (sampleSize <= 1000) {
  //   return { min: 10, max: 20, default: 15 };
  // }
  return { min: 8, max: 12, default: 10 };
}

/**
 * Cosine similarity between two Float32Array vectors.
 */
function cosineSimilarity(a: Float32Array, b: Float32Array): number {
  let dot = 0;
  let magA = 0;
  let magB = 0;
  for (let i = 0; i < a.length; i++) {
    dot += a[i] * b[i];
    magA += a[i] * a[i];
    magB += b[i] * b[i];
  }
  magA = Math.sqrt(magA);
  magB = Math.sqrt(magB);
  if (magA === 0 || magB === 0 || isNaN(magA) || isNaN(magB)) {
    return 0;
  }
  return dot / (magA * magB);
}

/**
 * Cosine distance = 1 - cosine similarity.
 */
function cosineDistance(a: Float32Array, b: Float32Array): number {
  return 1 - cosineSimilarity(a, b);
}

/**
 * L2-normalize a vector in-place, returning the same array.
 */
function l2Normalize(vec: Float32Array): Float32Array {
  let norm = 0;
  for (let i = 0; i < vec.length; i++) {
    norm += vec[i] * vec[i];
  }
  norm = Math.sqrt(norm);
  if (norm > 0) {
    for (let i = 0; i < vec.length; i++) {
      vec[i] /= norm;
    }
  }
  return vec;
}

/**
 * Compute the mean of a set of vectors, then L2-normalize to get a unit-length centroid.
 * Using mean + normalize is the correct approach for cosine-distance K-means.
 */
function computeCentroid(vectors: Float32Array[], dims: number): Float32Array {
  const mean = new Float32Array(dims);
  if (vectors.length === 0) {
    return mean;
  }
  for (const v of vectors) {
    for (let d = 0; d < dims; d++) {
      mean[d] += v[d];
    }
  }
  for (let d = 0; d < dims; d++) {
    mean[d] /= vectors.length;
  }
  return l2Normalize(mean);
}

/**
 * K-means++ initialization: selects k initial centroids from the data
 * with probability proportional to D(x)² (squared cosine distance to nearest existing centroid).
 */
function kMeansPlusPlusInit(data: Float32Array[], k: number): Float32Array[] {
  const n = data.length;
  const centroids: Float32Array[] = [];

  // 1. Choose first centroid uniformly at random
  const firstIndex = Math.floor(seededRandom() * n);
  centroids.push(new Float32Array(data[firstIndex]));

  // Distance from each point to nearest centroid
  const minDist = new Float32Array(n).fill(Infinity);

  for (let c = 1; c < k; c++) {
    const lastCentroid = centroids[c - 1];

    // Update minimum distances
    let totalWeight = 0;
    for (let i = 0; i < n; i++) {
      const dist = cosineDistance(data[i], lastCentroid);
      if (dist < minDist[i]) {
        minDist[i] = dist;
      }
      totalWeight += minDist[i] * minDist[i];
    }

    // Sample next centroid with probability proportional to D(x)²
    if (totalWeight === 0) {
      // All points are identical to existing centroids — pick random
      const idx = Math.floor(seededRandom() * n);
      centroids.push(new Float32Array(data[idx]));
      continue;
    }

    let r = seededRandom() * totalWeight;
    let selectedIndex = 0;
    for (let i = 0; i < n; i++) {
      r -= minDist[i] * minDist[i];
      if (r <= 0) {
        selectedIndex = i;
        break;
      }
    }
    centroids.push(new Float32Array(data[selectedIndex]));
  }

  return centroids;
}

/**
 * Generates a random projection matrix using a Gaussian distribution.
 * Used to reduce high-dimensional embeddings (e.g., 384) to a lower dimension (e.g., 32)
 * while preserving relative distances between points (Johnson-Lindenstrauss lemma).
 */
function generateRandomProjectionMatrix(originalDims: number, targetDims: number): Float32Array[] {
  const matrix: Float32Array[] = [];
  // Standard deviation for the Gaussian distribution
  const stdDev = 1 / Math.sqrt(targetDims);

  for (let i = 0; i < targetDims; i++) {
    const row = new Float32Array(originalDims);
    for (let j = 0; j < originalDims; j++) {
      // Box-Muller transform to generate normally distributed random numbers
      let u = 0,
        v = 0;
      while (u === 0) {
        u = seededRandom();
      } // Converting [0,1) to (0,1)
      while (v === 0) {
        v = seededRandom();
      }
      const num = Math.sqrt(-2.0 * Math.log(u)) * Math.cos(2.0 * Math.PI * v);
      row[j] = num * stdDev;
    }
    matrix.push(row);
  }
  return matrix;
}

/**
 * Projects a set of high-dimensional vectors into a lower-dimensional space
 * using the provided projection matrix.
 */
function projectData(data: Float32Array[], matrix: Float32Array[]): Float32Array[] {
  const n = data.length;
  const targetDims = matrix.length;
  const originalDims = data[0].length;
  const projectedData: Float32Array[] = new Array(n);

  for (let i = 0; i < n; i++) {
    const originalVec = data[i];
    const projectedVec = new Float32Array(targetDims);
    for (let j = 0; j < targetDims; j++) {
      const matrixRow = matrix[j];
      let sum = 0;
      for (let k = 0; k < originalDims; k++) {
        sum += originalVec[k] * matrixRow[k];
      }
      projectedVec[j] = sum;
    }
    projectedData[i] = projectedVec;
  }

  return projectedData;
}

/**
 * Run K-means clustering on 384-dimensional embedding vectors using cosine distance.
 *
 * @param data Array of Float32Array vectors (each 384-dim)
 * @param config K-means configuration (k, maxIterations, tolerance)
 * @returns KMeansResult with centroids, assignments, iterations, and inertia
 */
export function runKMeans(data: Float32Array[], config: KMeansConfig): KMeansResult {
  const { k, maxIterations = 100, nInit = 5, targetDims = 32 } = config;
  const n = data.length;
  const originalDims = data[0]?.length || 384;

  if (n === 0 || k <= 0) {
    return { centroids: [], assignments: [], iterations: 0, inertia: 0 };
  }

  const effectiveK = Math.min(k, n);

  // Apply Random Projection if requested and beneficial
  let workingData = data;
  let dims = originalDims;
  if (targetDims > 0 && targetDims < originalDims) {
    const projectionMatrix = generateRandomProjectionMatrix(originalDims, targetDims);
    workingData = projectData(data, projectionMatrix);
    dims = targetDims;
  }

  let bestInertia = Infinity;
  let bestResult: KMeansResult = { centroids: [], assignments: [], iterations: 0, inertia: Infinity };

  for (let init = 0; init < nInit; init++) {
    // Initialize centroids with K-means++
    let centroids = kMeansPlusPlusInit(workingData, effectiveK);
    let assignments = new Array<number>(n).fill(0);
    let iterations = 0;

    for (let iter = 0; iter < maxIterations; iter++) {
      iterations = iter + 1;

      // Assignment step: assign each point to nearest centroid (by cosine distance)
      const newAssignments: number[] = new Array(n);
      for (let i = 0; i < n; i++) {
        let bestCluster = 0;
        let bestDist = Infinity;
        for (let j = 0; j < effectiveK; j++) {
          const dist = cosineDistance(workingData[i], centroids[j]);
          if (dist < bestDist) {
            bestDist = dist;
            bestCluster = j;
          }
        }
        newAssignments[i] = bestCluster;
      }

      // Update step: recompute centroids as mean + L2-normalize
      const newCentroids: Float32Array[] = [];
      for (let j = 0; j < effectiveK; j++) {
        const clusterVectors: Float32Array[] = [];
        for (let i = 0; i < n; i++) {
          if (newAssignments[i] === j) {
            clusterVectors.push(workingData[i]);
          }
        }
        if (clusterVectors.length > 0) {
          newCentroids.push(computeCentroid(clusterVectors, dims));
        } else {
          // Empty cluster — reinitialize with random point
          newCentroids.push(new Float32Array(workingData[Math.floor(seededRandom() * n)]));
        }
      }

      // Check convergence: did any assignments change?
      let assignmentsChanged = false;
      for (let i = 0; i < n; i++) {
        if (assignments[i] !== newAssignments[i]) {
          assignmentsChanged = true;
          break;
        }
      }

      centroids = newCentroids;
      assignments = newAssignments;

      if (!assignmentsChanged) {
        break;
      }
    }

    // Calculate inertia (sum of squared cosine distances to assigned centroids)
    let inertia = 0;
    for (let i = 0; i < n; i++) {
      const dist = cosineDistance(workingData[i], centroids[assignments[i]]);
      inertia += isNaN(dist) ? 0 : dist * dist;
    }

    if (inertia < bestInertia) {
      bestInertia = inertia;
      bestResult = { centroids, assignments, iterations, inertia };
    }
  }

  // If we used random projection, we need to recompute the final centroids
  // in the original 384-dimensional space so that `buildClusterSuggestions`
  // can correctly calculate similarities against the original embeddings.
  // NOTE: This is now handled in findOptimalK to avoid redundant projections
  if (targetDims > 0 && targetDims < originalDims) {
    const finalCentroids: Float32Array[] = [];
    for (let j = 0; j < effectiveK; j++) {
      const clusterVectors: Float32Array[] = [];
      for (let i = 0; i < n; i++) {
        if (bestResult.assignments[i] === j) {
          clusterVectors.push(data[i]);
        }
      }
      if (clusterVectors.length > 0) {
        finalCentroids.push(computeCentroid(clusterVectors, originalDims));
      } else {
        finalCentroids.push(new Float32Array(data[Math.floor(seededRandom() * n)]));
      }
    }
    bestResult.centroids = finalCentroids;
  }

  return bestResult;
}

/**
 * Build ClusterSuggestion objects from K-means results.
 * For each centroid, finds the top 10 nearest traits by cosine similarity.
 *
 * @param kmeansResult K-means output (centroids, assignments)
 * @param allTraits Array of trait objects with text and type
 * @param sampledEmbeddings Embeddings of the sampled traits (same order as allTraits)
 * @returns Array of ClusterSuggestion objects
 */
export function buildClusterSuggestions(
  kmeansResult: KMeansResult,
  allTraits: { text: string; type: 'strength' | 'weakness' }[],
  sampledEmbeddings: Float32Array[],
): ClusterSuggestion[] {
  const { centroids, assignments } = kmeansResult;

  return centroids.map((centroid, clusterId) => {
    // Collect traits assigned to this cluster with their similarity scores
    const clusterTraits: { text: string; type: 'strength' | 'weakness'; similarity: number }[] = [];

    for (let i = 0; i < assignments.length; i++) {
      if (assignments[i] === clusterId) {
        clusterTraits.push({
          text: allTraits[i].text,
          type: allTraits[i].type,
          similarity: parseFloat(cosineSimilarity(sampledEmbeddings[i], centroid).toFixed(3)),
        });
      }
    }

    // Sort by similarity descending, take top 10
    clusterTraits.sort((a, b) => b.similarity - a.similarity);

    return {
      id: clusterId,
      topTraits: clusterTraits.slice(0, 10),
    };
  });
}

/**
 * Random sample up to `maxSize` indices from an array of given length.
 * If the array length is <= maxSize, returns all indices.
 */
export function sampleIndices(totalLength: number, maxSize: number): number[] {
  if (totalLength <= maxSize) {
    return Array.from({ length: totalLength }, (_, i) => i);
  }
  return Array.from({ length: totalLength }, (_, i) => i)
    .sort(() => seededRandom() - 0.5)
    .slice(0, maxSize);
}

export interface OptimalKResult {
  /** The k value with the highest silhouette score */
  bestK: number;
  /** K-means result for the best k */
  bestResult: KMeansResult;
  /** Silhouette scores for each tested k value */
  scores: { k: number; silhouette: number }[];
}

/**
 * Mean silhouette score for a clustering result.
 *
 * For each point i:
 *   a(i) = mean cosine distance to all other points in the same cluster
 *   b(i) = min over other clusters of (mean cosine distance to points in that cluster)
 *   s(i) = (b(i) - a(i)) / max(a(i), b(i))
 *
 * Returns the mean of s(i) across all points. Range: [-1, 1], higher = better.
 *
 * Optimized: precomputes a pairwise distance matrix once, then indexes into it.
 */
export function silhouetteScore(
  data: Float32Array[],
  assignments: number[],
  k: number,
  precomputedDist?: Float32Array,
): number {
  const n = data.length;
  if (n <= 1 || k <= 1) {
    return 0;
  }

  // Build cluster membership lists
  const clusterMembers: number[][] = Array.from({ length: k }, () => []);
  for (let i = 0; i < n; i++) {
    clusterMembers[assignments[i]].push(i);
  }

  // Precompute full pairwise cosine distance matrix (symmetric, upper-triangle stored)
  // For 2000 points this is ~4M distances — fast with Float32.
  // We only compute this ONCE and pass it in, or compute it here if not provided.
  let dist = precomputedDist;
  if (!dist) {
    dist = new Float32Array(n * n);
    for (let i = 0; i < n; i++) {
      for (let j = i + 1; j < n; j++) {
        const d = cosineDistance(data[i], data[j]);
        dist[i * n + j] = d;
        dist[j * n + i] = d;
      }
    }
  }

  let totalSilhouette = 0;
  let validPoints = 0;

  for (let i = 0; i < n; i++) {
    const ci = assignments[i];
    const myCluster = clusterMembers[ci];

    // a(i): mean distance to same-cluster points (excluding self)
    if (myCluster.length <= 1) {
      // Single-point cluster — silhouette is 0
      continue;
    }

    let aSum = 0;
    for (const j of myCluster) {
      if (j !== i) {
        aSum += dist[i * n + j];
      }
    }
    const ai = aSum / (myCluster.length - 1);

    // b(i): min mean distance to any other cluster
    let bi = Infinity;
    for (let c = 0; c < k; c++) {
      if (c === ci || clusterMembers[c].length === 0) {
        continue;
      }
      let bSum = 0;
      for (const j of clusterMembers[c]) {
        bSum += dist[i * n + j];
      }
      const meanDist = bSum / clusterMembers[c].length;
      if (meanDist < bi) {
        bi = meanDist;
      }
    }

    const maxAB = Math.max(ai, bi);
    if (maxAB > 0) {
      totalSilhouette += (bi - ai) / maxAB;
    }
    validPoints++;
  }

  return validPoints > 0 ? totalSilhouette / validPoints : 0;
}

/**
 * Find the optimal k by running K-means for each candidate in the range (step=2)
 * and selecting the k with the highest mean silhouette score.
 *
 * Falls back to getKRange().default if all scores are non-positive.
 *
 * @param data       Embedding vectors (Float32Array[])
 * @param sampleSize Number of sampled vectors (used for getKRange)
 * @returns          OptimalKResult with bestK, bestResult, and all scores
 */
export function findOptimalK(data: Float32Array[], sampleSize: number): OptimalKResult {
  // Reset the random seed at the start of every clustering run.
  // This guarantees that if the user runs the analysis twice on the exact same data,
  // they will get the exact same clusters, providing a stable UX.
  resetKMeansSeed();

  const range = getKRange(sampleSize);
  const candidates: number[] = [];

  // Build candidate list with step=1, always including min and max
  for (let k = range.min; k <= range.max; k += 1) {
    candidates.push(k);
  }
  if (candidates[candidates.length - 1] !== range.max) {
    candidates.push(range.max);
  }

  const scores: { k: number; silhouette: number }[] = [];
  let bestK = range.default;
  let bestScore = -Infinity;
  let bestResult: KMeansResult = { centroids: [], assignments: [], iterations: 0, inertia: 0 };

  // 1. Generate Random Projection Matrix ONCE for all K candidates
  const originalDims = data[0]?.length || 384;
  const targetDims = 64; // 64 is a sweet spot for Johnson-Lindenstrauss on ~1000 points
  let workingData = data;
  let projectionMatrix: Float32Array[] | null = null;

  if (targetDims > 0 && targetDims < originalDims) {
    projectionMatrix = generateRandomProjectionMatrix(originalDims, targetDims);
    workingData = projectData(data, projectionMatrix);
  }

  // Precompute distance matrix ONCE for silhouette score
  const n = data.length;
  const distMatrix = new Float32Array(n * n);
  for (let i = 0; i < n; i++) {
    for (let j = i + 1; j < n; j++) {
      const d = cosineDistance(data[i], data[j]);
      distMatrix[i * n + j] = d;
      distMatrix[j * n + i] = d;
    }
  }

  for (const k of candidates) {
    // Pass the pre-projected data to runKMeans
    // targetDims=0 because it's already projected
    const result = runKMeans(workingData, { k, nInit: 5, targetDims: 0 });

    // If we used random projection, we need to recompute the final centroids
    // in the original 384-dimensional space so that `buildClusterSuggestions`
    // can correctly calculate similarities against the original embeddings.
    if (projectionMatrix) {
      const finalCentroids: Float32Array[] = [];
      for (let j = 0; j < result.centroids.length; j++) {
        const clusterVectors: Float32Array[] = [];
        for (let i = 0; i < data.length; i++) {
          if (result.assignments[i] === j) {
            clusterVectors.push(data[i]);
          }
        }
        if (clusterVectors.length > 0) {
          finalCentroids.push(computeCentroid(clusterVectors, originalDims));
        } else {
          finalCentroids.push(new Float32Array(data[Math.floor(seededRandom() * data.length)]));
        }
      }
      result.centroids = finalCentroids;
    }

    const score = silhouetteScore(data, result.assignments, k, distMatrix);
    scores.push({ k, silhouette: score });

    if (score > bestScore) {
      bestScore = score;
      bestK = k;
      bestResult = result;
    }
  }

  // If no positive score found, fall back to default k
  if (bestScore <= 0) {
    bestResult = runKMeans(workingData, { k: range.default, nInit: 5, targetDims: 0 });
    if (projectionMatrix) {
      const finalCentroids: Float32Array[] = [];
      for (let j = 0; j < bestResult.centroids.length; j++) {
        const clusterVectors: Float32Array[] = [];
        for (let i = 0; i < data.length; i++) {
          if (bestResult.assignments[i] === j) {
            clusterVectors.push(data[i]);
          }
        }
        if (clusterVectors.length > 0) {
          finalCentroids.push(computeCentroid(clusterVectors, originalDims));
        } else {
          finalCentroids.push(new Float32Array(data[Math.floor(seededRandom() * data.length)]));
        }
      }
      bestResult.centroids = finalCentroids;
    }
    bestK = range.default;
  }

  return { bestK, bestResult, scores };
}
